// FanChart.js — Recharts AreaChart showing scenario fan band + benchmark
import React, { useState } from 'react';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, ComposedChart, Legend,
} from 'recharts';

/**
 * Format currency for tooltip.
 */
function fmtVal(v, ccy) {
  if (v === null || v === undefined) return '--';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  const sym = ccy === 'GBP' ? '\u00a3' : ccy === 'EUR' ? '\u20ac' : '$';
  if (abs >= 1e6) return `${sign}${sym}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${sym}${(abs / 1e3).toFixed(0)}K`;
  return `${sign}${sym}${abs.toFixed(0)}`;
}

/**
 * Custom tooltip with tabular numerics.
 */
const ChartTooltip = ({ active, payload, label, baseCurrency }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-none p-3 shadow-sm">
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">
        Year {label}
      </p>
      {payload.map((entry, i) => (
        entry.value !== undefined && entry.name !== 'adverseFloor' && (
          <div key={i} className="flex justify-between gap-4">
            <span className="font-mono text-[11px] text-zinc-600">{entry.name}</span>
            <span className="font-mono text-[11px] tabular-nums font-medium" style={{ color: entry.color }}>
              {fmtVal(entry.value, baseCurrency)}
            </span>
          </div>
        )
      ))}
    </div>
  );
};

/**
 * Fan chart showing adverse-to-optimistic range with base line and benchmark.
 * @param {{ result: Object, baseCurrency: string, adverseShockYear: number }} props
 */
const FanChart = ({ result, baseCurrency = 'USD', adverseShockYear = 3 }) => {
  const [showHedged, setShowHedged] = useState(true);

  if (!result || !result.scenarios) return null;

  const { scenarios, benchmark } = result;
  const valueKey = showHedged ? 'valueHedged' : 'valueUnhedged';

  // Build chart data
  const horizon = result.meta.horizon;
  const chartData = [];

  for (let t = 0; t <= horizon; t++) {
    const point = { year: t };

    // Base
    if (scenarios.base) {
      point.base = scenarios.base.years[t]?.[valueKey] || 0;
    }
    // Optimistic
    if (scenarios.optimistic) {
      point.optimistic = scenarios.optimistic.years[t]?.[valueKey] || 0;
    }
    // Adverse
    if (scenarios.adverse) {
      point.adverse = scenarios.adverse.years[t]?.[valueKey] || 0;
    }
    // Custom
    if (scenarios.custom) {
      point.custom = scenarios.custom.years[t]?.[valueKey] || 0;
    }

    // Fan band (adverse floor and optimistic - adverse for stacked area)
    if (scenarios.adverse && scenarios.optimistic) {
      point.adverseFloor = scenarios.adverse.years[t]?.[valueKey] || 0;
      point.fanBand = (scenarios.optimistic.years[t]?.[valueKey] || 0) - (scenarios.adverse.years[t]?.[valueKey] || 0);
    }

    // Benchmark
    if (benchmark && benchmark.years[t]) {
      point.benchmark = benchmark.years[t].value;
    }

    chartData.push(point);
  }

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
          Scenario Fan Chart
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHedged(true)}
            className={`font-mono text-[10px] px-2 py-1 border rounded-none ${
              showHedged ? 'bg-zinc-600 text-white border-zinc-600' : 'text-zinc-500 border-zinc-300 hover:bg-zinc-50'
            }`}
            aria-label="Show hedged values"
          >
            Hedged
          </button>
          <button
            onClick={() => setShowHedged(false)}
            className={`font-mono text-[10px] px-2 py-1 border rounded-none ${
              !showHedged ? 'bg-zinc-600 text-white border-zinc-600' : 'text-zinc-500 border-zinc-300 hover:bg-zinc-50'
            }`}
            aria-label="Show unhedged values"
          >
            Unhedged
          </button>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          <XAxis
            dataKey="year"
            tick={{ fontSize: 10, fontFamily: 'monospace', fill: '#71717a' }}
            label={{ value: 'Year', position: 'insideBottom', offset: -2, fontSize: 10, fontFamily: 'monospace', fill: '#71717a' }}
          />
          <YAxis
            tick={{ fontSize: 10, fontFamily: 'monospace', fill: '#71717a' }}
            tickFormatter={v => fmtVal(v, baseCurrency)}
            width={70}
          />
          <Tooltip content={<ChartTooltip baseCurrency={baseCurrency} />} />

          {/* Fan band: stacked area with invisible base + visible range */}
          {scenarios.adverse && scenarios.optimistic && (
            <>
              <Area
                type="monotone"
                dataKey="adverseFloor"
                stackId="fan"
                fill="transparent"
                stroke="none"
                name="adverseFloor"
              />
              <Area
                type="monotone"
                dataKey="fanBand"
                stackId="fan"
                fill="#e0e7ff"
                fillOpacity={0.5}
                stroke="none"
                name="Range"
              />
            </>
          )}

          {/* Base line (primary) */}
          {scenarios.base && (
            <Line
              type="monotone"
              dataKey="base"
              stroke="#3832e3"
              strokeWidth={2.5}
              dot={false}
              name="Base"
            />
          )}

          {/* Benchmark (dashed) */}
          {benchmark && (
            <Line
              type="monotone"
              dataKey="benchmark"
              stroke="#a1a1aa"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              dot={false}
              name="Benchmark"
            />
          )}

          {/* Custom scenario */}
          {scenarios.custom && (
            <Line
              type="monotone"
              dataKey="custom"
              stroke="#7b7eff"
              strokeWidth={1.5}
              strokeDasharray="4 2"
              dot={false}
              name="Custom"
            />
          )}

          {/* Shock year reference line */}
          {scenarios.adverse && adverseShockYear > 0 && (
            <ReferenceLine
              x={adverseShockYear}
              stroke="#ef4444"
              strokeDasharray="4 4"
              strokeWidth={1}
              label={{ value: 'Shock', position: 'top', fontSize: 9, fontFamily: 'monospace', fill: '#ef4444' }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-3 px-2">
        <div className="flex items-center gap-1">
          <div className="w-4 h-0.5 bg-[#3832e3]" />
          <span className="font-mono text-[10px] text-zinc-500">Base</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-3 bg-indigo-100 opacity-50" />
          <span className="font-mono text-[10px] text-zinc-500">Adverse-Optimistic Range</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-0.5 bg-zinc-400" style={{ borderTop: '1.5px dashed #a1a1aa' }} />
          <span className="font-mono text-[10px] text-zinc-500">Benchmark</span>
        </div>
        {scenarios.custom && (
          <div className="flex items-center gap-1">
            <div className="w-4 h-0.5 bg-[#7b7eff]" />
            <span className="font-mono text-[10px] text-zinc-500">Custom</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default FanChart;
